import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';
import { UserPlus, AlertCircle, CalendarClock, Mail, CheckCircle2 } from 'lucide-react';
import { publicPortalApi, type PublicSignupState } from '../api/customerPortal';

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupState, setSignupState] = useState<PublicSignupState | null>(null);
  const [newsEmail, setNewsEmail] = useState('');
  const [newsBusy, setNewsBusy] = useState(false);
  const [newsDone, setNewsDone] = useState(false);

  // Owner-controlled signup gate (Customer Portal → Signup).
  useEffect(() => {
    publicPortalApi.signupState().then(setSignupState).catch(() => setSignupState(null));
  }, []);

  const joinNewsletter = async (e: FormEvent) => {
    e.preventDefault();
    if (!newsEmail.trim()) return;
    setNewsBusy(true);
    setError('');
    try {
      await publicPortalApi.newsletterSignup(newsEmail.trim());
      setNewsDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not sign up for the newsletter.');
    } finally {
      setNewsBusy(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (username.trim().length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      await register(email.trim(), username.trim(), password, displayName.trim() || undefined);
      navigate('/app');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) setError(detail.message);
      else setError(detail || err?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const signupClosed = signupState && !signupState.signup_enabled;

  // While signup is closed, show the owner's message + newsletter signup
  // instead of the registration form.
  if (signupClosed) {
    const message = signupState?.signup_message
      || 'Our official launch date is November 1. Sign up for the newsletter and we will notify you when we open.';
    return (
      <div className="flex min-h-screen items-center justify-center bg-white dark:bg-slate-900 p-4">
        <div className="w-full max-w-md">
          <div className="mb-8 flex flex-col items-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-purple-600 shadow-lg shadow-brand-500/30" aria-hidden="true">
              <CalendarClock size={26} className="text-white" />
            </div>
            <h1 className="text-center text-2xl font-bold text-slate-950 dark:text-slate-100">Coming Soon</h1>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 shadow-xl dark:border-slate-800 dark:bg-slate-800/50">
            {error && (
              <div role="alert" className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400">
                <AlertCircle size={16} />{error}
              </div>
            )}
            {signupState?.launch_at && (
              <p className="mb-3 text-center text-sm font-medium text-brand-600 dark:text-brand-400">
                Launching {new Date(`${signupState.launch_at.slice(0, 10)}T12:00:00Z`).toLocaleDateString('en', { month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
              </p>
            )}
            <p className="text-center text-sm text-slate-700 dark:text-slate-300">{message}</p>

            {signupState?.newsletter_open && (
              newsDone ? (
                <div className="mt-5 flex items-center justify-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400">
                  <CheckCircle2 size={16} />You are on the list. We will notify you.
                </div>
              ) : (
                <form onSubmit={joinNewsletter} className="mt-5 space-y-3">
                  <label htmlFor="newsletter-email" className="block text-sm font-medium text-slate-800 dark:text-slate-300">
                    <Mail size={14} className="mr-1 inline" />Get notified
                  </label>
                  <input
                    id="newsletter-email"
                    type="email"
                    required
                    value={newsEmail}
                    onChange={e => setNewsEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-950 placeholder-slate-600 focus:border-brand-500 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
                  />
                  <Button type="submit" disabled={newsBusy} className="w-full">
                    {newsBusy ? <LoadingSpinner size="sm" /> : <Mail size={18} />}
                    {newsBusy ? 'Signing up...' : 'Notify me'}
                  </Button>
                </form>
              )
            )}
          </div>

          <p className="mt-4 text-center text-sm text-slate-700 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-brand-700 dark:text-brand-400 hover:text-brand-300">Sign In</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-white dark:bg-slate-900 p-4">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-purple-600 shadow-lg shadow-brand-500/30" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="register-logo" x1="0" y1="0" x2="64" y2="64" gradientUnits="userSpaceOnUse">
                  <stop stopColor="#c7d2fe" />
                  <stop offset="1" stopColor="#e9d5ff" />
                </linearGradient>
              </defs>
              <path d="M16 40 L20 24 L26 32 L32 20 L38 32 L44 24 L48 40 Z" fill="url(#register-logo)" opacity="0.98" />
              <path d="M16 40 L48 40 L46 44 L18 44 Z" fill="white" opacity="0.85" />
              <circle cx="32" cy="36" r="3" fill="url(#register-logo)" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-950 dark:text-slate-100">Create Account</h1>
          <p className="mt-1 text-sm text-slate-700 dark:text-slate-400">Join SAIK Dashboard</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 p-6 shadow-xl"
        >
          {error && (
            <div role="alert" className="mb-4 flex items-center gap-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2.5 text-sm text-red-700 dark:text-red-400">
              <AlertCircle size={16} aria-hidden="true" />
              {error}
            </div>
          )}

          <div className="mb-4">
            <label htmlFor="register-email" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Email
            </label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              autoComplete="email"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="you@example.com"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="register-username" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Username
            </label>
            <input
              id="register-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              minLength={3}
              autoComplete="username"
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Choose a username"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="register-name" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Display name <span className="text-slate-500">(optional)</span>
            </label>
            <input
              id="register-name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={50}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="How should we greet you?"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="register-password" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Password
            </label>
            <input
              id="register-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="At least 6 characters"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="register-confirm" className="mb-1.5 block text-sm font-medium text-slate-800 dark:text-slate-300">
              Confirm Password
            </label>
            <input
              id="register-confirm"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2.5 text-sm text-slate-950 dark:text-slate-100 placeholder-slate-600 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              placeholder="Re-enter password"
            />
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            {loading ? <LoadingSpinner size="sm" /> : <UserPlus size={18} />}
            {loading ? 'Creating account...' : 'Register'}
          </Button>

          <p className="mt-4 text-center text-sm text-slate-700 dark:text-slate-400">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-brand-700 dark:text-brand-400 hover:text-brand-300">
              Sign In
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
